import './datadog-rum';
import { DebtScheduleState } from 'ng2-app/store/debt-schedule/debt-schedule.state';
import { FlashSpreadState } from 'ng2-app/store/flashspread/flashspread.state';
import { DscrState } from 'ng2-app/store/profit-loss-statements/dscr.state';

// Modules
import { AddNewLeadModule } from './add-new-lead/add-new-lead.module';
import { AppRoutingModule } from './app-routing.module';
import { BorrowerModule } from './modules/borrower/borrower.module';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { CallbarModule } from './callbar/callbar.module';
import { ClaimLeadBarModule } from './claim-lead-bar/claim-lead-bar.module';
import { GlobalBorrowerTabsModule } from './global-borrower-tabs/global-borrower-tabs.module';
import { GlobalHeaderModule } from './global-header/global-header.module';
import { CxInteractionSidebarModule } from './cx-interaction/cx-interaction-form.module';
import { HttpClientModule } from '@angular/common/http';
import { DEFAULT_CURRENCY_CODE, ErrorHandler, LOCALE_ID, NgModule } from '@angular/core';
import { NgxsLoggerPluginModule } from '@ngxs/logger-plugin';
import { NgxsModule } from '@ngxs/store';
import { NgxsReduxDevtoolsPluginModule } from '@ngxs/devtools-plugin';
import { NotificationsModule } from './notifications/notifications.module';
import { NotificationsHistoryModule } from './notifications-history/notifications-history.module';
import { OwnerApplicationModule } from './modules/owner-application/owner-application.module';
import { ReactiveFormsModule } from '@angular/forms';
import Quill from 'quill';
import { RollbarService, rollbarFactory, RollbarErrorHandler } from './rollbar';

// Services
import { BorrowerValueService } from './services/borrower-value/borrower-value.service';
import { DealFlagService } from './services/deal-flag/deal-flag.service';
import { DealNoteService } from './services/dealNotes/dealNote.service';
import { DocumentsService } from './services/documents/documents.service';
import { EventBusService } from './modules/shared/services/event-bus.service';
import { FunnelService } from './modules/funnel/services/funnel.service';
import { LinkService } from './services/links/links.service';
import { IndustriesService } from './services/industries/industries.service';
import { LocaleSettingService } from './services/i18n/locale-setting.service';
import { MessageService } from './messages/message-service/message.service';
import { NotesService } from './services/notes/notes.service';
import { PerformanceService } from './services/performance/performance.service';
import { PhoneLocaleService } from './services/phone-locale.service';
import { PusherService } from './modules/shared/services/pusher.service';
import { QueueService } from './services/queue/queue.service';
import { SessionService } from './modules/session/services/session.service';
import { SmsService } from './sms/sms-service/sms.service';
import { TaskService } from './modules/task-management/services/task.service';
import { ApplicationReviewService } from './services/application-review/application-review.service';
import { CreditReportsService } from './services/credit-reports/credit-reports.service';
import { TeamDealsService, LenderUserDealsService } from './modules/team-deals/services/team-deals.service';
import { TeamService } from './modules/teams/team.service';

// NGXS State
import { ActivityState } from './store/activity/activity.state';
import { AdminPhoneToolsState } from './store/admin-phone-tools/admin-phone-tools.state';
import { ApplicationReviewState } from './store/borrower-fd/application-review/application-review.state';
import { ApplicationState } from './store/deal/application/application.state';
import { ApprovalsState } from './store/offers/approvals.state';
import { BankAccountState } from './store/bank-account/bank-account.state';
import { BorrowerAlertsState } from './store/borrower/alerts/borrower-alerts.state';
import { BorrowerIndexState } from './store/borrower-index/borrower-index.state';
import { BorrowerLoansState } from './store/borrower/borrower-loans/borrower-loans.state';
import { BorrowerOpportunityHistoryState } from './store/borrower/opportunity-history/borrower-opportunity-history.state';
import { BorrowerState } from './store/borrower/borrower/borrower.state';
import { BorrowerStatusReasonState } from './store/borrower-status-reason/borrower-status-reason.state';
import { BorrowerValueState } from './store/borrower-value/borrower-value.state';
import { CallState } from './store/call/call.state';
import { ChatState } from './chat/chat-store/chat.state';
import { ClaimLeadState } from './store/claim-lead/claim-lead.state';
import { ContactState } from './store/contact/contact.state';
import { ContractRequestState } from 'ng2-app/services/contract-requests/contract-request.state';
import { CountryDataState } from './store/country/country-data.state';
import { CreditBoxesState } from './store/credit-boxes/credit-boxes.state';
import { CreditReportsState } from './store/credit-reports/credit-reports.state';
import { CXInteractionState } from './store/cx-interaction/cx-interaction.state';
import { CXPerformanceState } from './store/cx-performance/cx-performance.state';
import { DealAlertsState } from './store/deal/deal-alerts/deal-alerts.state';
import { DealApplicationRequirementState } from './store/deal/deal-application-requirements/deal-application-requirement.state';
import { DealDocumentState } from './store/deal/deal-documents/deal-document.state';
import { DealFlagState } from './store/deal-flag/deal-flag.state';
import { DealNoteState } from './store/dealNotes/dealNote.state';
import { DealState } from './store/deal/deal/deal.state';
import { DecisioningState } from './store/decisioning/decisioning.state';
import { DocumentState } from './store/documents/document.state';
import { DrawDealState } from './store/draw-deal/draw-deal.state';
import { EmailTemplateState } from './store/lender-user/email-templates/email-templates.state';
import { FundingDeskFunnelState } from './store/funnel/funding-desk-funnel.state';
import { FundingManagerFunnelState } from './store/funnel/funding-manager-funnel.state';
import { GlobalBorrowerTabsState } from './store/global-borrower-tabs/global-borrower-tabs.state';
import { LeadAssignmentState } from './store/lead-assignment/lead-assignment.state';
import { ErcLinkedBusinessState } from './store/erc-linked-business/erc-linked-business.state';
import { GlobalClaimCapDataState } from './store/global-claim-cap/global-claim-cap.state';
import { LenderUserState } from './store/lender-user/lender-user/lender-user.state';
import { LoanProductsState } from './store/loan-product/loan-product.state';
import { MessageState } from './messages/message-store/message.state';
import { NonOwnerPersonnelState } from './store/non-owner-personnel/non-owner-personnel.state';
import { NoteState } from './store/notes/note.state';
import { NotificationState } from './store/notification/notification.state';
import { OfferOptionsState } from './store/offers/offer-options.state';
import { OfferState } from './store/offers/offer.state';
import { OpportunityState } from './store/opportunity/opportunity.state';
import { PayrollValidationState } from './store/payroll-validation/payroll-validation.state';
import { PerformanceState } from './store/performance/performance.state';
import { QuestionsState } from './store/question/question.state';
import { QueueState } from './store/queue/queue.state';
import { ReminderState } from './store/reminder/reminder.state';
import { SettingsAlertsState } from './store/settings/alerts/settings-alerts.state';
import { SmsState } from './sms/sms-store/sms.state';
import { SmsTemplateState } from './store/lender-user/sms-templates/sms-templates.state';
import { StateState } from './store/state/state.state';
import { TeamDealsState } from './store/team-deals/team-deals.state';
import { TeamState } from './store/team/team.state';
import { UserDirectoryState } from './store/user-directory/user-directory.state';
import { WorkflowState } from './store/workflow/workflow.state';
import { WorkflowTasksState } from 'ng2-app/services/workflow-tasks/workflow-tasks.state';

// Misc
import { ApiAuthOnlyHttpClient } from './http/clients/api-auth-only';
import { ApiHttpClient } from './http/clients/api';
import { AppComponent } from './app.component';
import { BpApiHttpClient } from './http/clients/bp-api';
import { CommonModule, CurrencyPipe, DatePipe, DecimalPipe } from '@angular/common';
import { environment } from 'environments/environment';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MAT_SNACK_BAR_DEFAULT_OPTIONS } from '@angular/material/snack-bar';
import { BorrowerPageModule } from './borrower/borrower-page.module';
import { StateService } from './modules/shared/services/state.service';
import { LoadingModule } from './loading/loading.module';
import { AuthenticationService } from './modules/session/services/authentication.service';
import { RouterModule } from '@angular/router';
import { TaskManagementState } from './store/task-management/task-management.state';
import { AlertModule } from './components/alert/alert.module';
import { TodayModule } from './modules/today/today.module';
import { BorrowerSidebarState } from 'ng2-app/borrower-sidebar/borrower-sidebar.state';
import { AuthErrorModule } from './auth-error/auth-error.module';
import { TemplatePickerModule } from './modules/template-picker/template-picker.module';
import { UserSmsThreadsState } from 'ng2-app/modules/today/state/sms/user-sms-state';
import { UserActionItemsState } from 'ng2-app/modules/today/state/user-action-items.state';
import { UserThreadsState } from 'ng2-app/modules/today/state/email/user-threads.state';
import { UserActionItemDetailState } from 'ng2-app/modules/today/state/user-action-item-detail.state';
import { UserCallHistoryState } from './modules/today/state/call-history/call-history.state';
import { TodayBadgeCountsState } from 'ng2-app/modules/today/state/today-badge-counts/today-badge-counts.state';
import { BorrowerStatusesState } from 'ng2-app/store/borrowerStatuses/borrower-statuses.state';
import { FundingNeedsState } from './store/funding-needs/funding-needs.state';
import { LetDirectiveModule } from 'ng2-app/modules/directives/let/let.directive.module';
import { ErcState } from 'ng2-app/store/erc/erc.state';
import { ActivityStreamState } from 'ng2-app/store/activity/activity-stream.state';
import { DuplicateBorrowersState } from './store/duplicate-borrowers/duplicate-borrowers.state';
import { TodayPageState } from 'ng2-app/modules/today/state/today-page-state/today-page.state';
import { OcrolusState } from './store/ocrolus/ocrolus.state';
import {ConfirmationDialogModule} from 'ng2-app/modules/confirmation-dialog/confirmation-dialog.module';
import { NgxMaskDirective, NgxMaskPipe, provideEnvironmentNgxMask, provideNgxMask } from 'ngx-mask';
import { FormlyModule } from '@ngx-formly/core';
import { FORMLY_CONFIG } from 'ng2-app/form-core/formly/formly-core.config';
import { FormlyMaterialModule } from '@ngx-formly/material';
import {
  StackableNotificationsModule
} from 'ng2-app/notifications/stackable-notifications/stackable-notifications.module';
import { StackableNotificationsState } from 'ng2-app/store/stackable-notifications/StackableNotifications.state';

// Quill Editor Settings
const parchment = Quill.import('parchment');
const block = parchment.query('block');
block.tagName = 'DIV';
Quill.register(block, true);

// Allows for setting window.[key] for debugging and testing
declare global {
  interface Window {
    [key: string]: any;
  }
}


@NgModule({
  declarations: [
    AppComponent
  ],
  imports: [
    FormlyMaterialModule,
    FormlyModule.forRoot( FORMLY_CONFIG ),
    AlertModule,
    AddNewLeadModule,
    AppRoutingModule,
    AuthErrorModule,
    BorrowerModule,
    BrowserAnimationsModule,
    CommonModule,
    ConfirmationDialogModule,
    CallbarModule,
    BorrowerPageModule,
    ClaimLeadBarModule,
    GlobalBorrowerTabsModule,
    GlobalHeaderModule,
    CxInteractionSidebarModule,
    HttpClientModule,
    LoadingModule,
    LetDirectiveModule,
    MatSidenavModule,
    NotificationsModule,
    NotificationsHistoryModule,
    OwnerApplicationModule,
    ReactiveFormsModule,
    RouterModule,
    TemplatePickerModule,
    TodayModule,
    NgxsModule.forRoot( [
        ActivityState,
        ActivityStreamState,
        AdminPhoneToolsState,
        ApplicationReviewState,
        ApplicationState,
        ApprovalsState,
        BankAccountState,
        BorrowerAlertsState,
        BorrowerIndexState,
        BorrowerLoansState,
        BorrowerOpportunityHistoryState,
        BorrowerSidebarState,
        BorrowerState,
        BorrowerStatusReasonState,
        BorrowerStatusesState,
        BorrowerValueState,
        CXInteractionState,
        CXPerformanceState,
        CallState,
        ChatState,
        ClaimLeadState,
        ContactState,
        ContractRequestState,
        CountryDataState,
        CreditBoxesState,
        CreditReportsState,
        DealAlertsState,
        DealApplicationRequirementState,
        DealDocumentState,
        DealFlagState,
        DealNoteState,
        DealState,
        DebtScheduleState,
        DecisioningState,
        DocumentState,
        DrawDealState,
        DuplicateBorrowersState,
        EmailTemplateState,
        ErcLinkedBusinessState,
        ErcState,
        FlashSpreadState,
        FundingDeskFunnelState,
        FundingManagerFunnelState,
        FundingNeedsState,
        GlobalBorrowerTabsState,
        GlobalClaimCapDataState,
        LeadAssignmentState,
        LenderUserState,
        LoanProductsState,
        MessageState,
        NonOwnerPersonnelState,
        NoteState,
        NotificationState,
        OcrolusState,
        OfferOptionsState,
        OfferState,
        OpportunityState,
        PayrollValidationState,
        PerformanceState,
        DscrState,
        QuestionsState,
        QueueState,
        ReminderState,
        SettingsAlertsState,
        SmsState,
        SmsTemplateState,
        StackableNotificationsState,
        StateState,
        TaskManagementState,
        TeamDealsState,
        TeamState,
        TodayBadgeCountsState,
        TodayPageState,
        UserActionItemDetailState,
        UserActionItemsState,
        UserCallHistoryState,
        UserDirectoryState,
        UserSmsThreadsState,
        UserThreadsState,
        WorkflowState,
        WorkflowTasksState,
      ],
      // { developmentMode: environment?.environment !== 'LIVE' }
    ),
    NgxsLoggerPluginModule.forRoot( {
      disabled: true
    } ),
    NgxsReduxDevtoolsPluginModule.forRoot( {
      // Install the Redux Devtools browser plugin to use this.
      // See https://lendio.atlassian.net/browse/SMART-799.
      disabled: environment?.environment === 'LIVE',
      maxAge: 5000
    } ),
    StackableNotificationsModule,
  ],
  providers: [
    ApiAuthOnlyHttpClient,
    ApiHttpClient,
    ApplicationReviewService,
    AuthenticationService,
    BorrowerValueService,
    BpApiHttpClient,
    CurrencyPipe,
    CreditReportsService,
    DatePipe,
    DecimalPipe,
    DocumentsService,
    EventBusService,
    FunnelService,
    HttpClientModule,
    IndustriesService,
    LinkService,
    LocaleSettingService,
    MessageService,
    NotesService,
    DealFlagService,
    DealNoteService,
    PerformanceService,
    PhoneLocaleService,
    PusherService,
    QueueService,
    SessionService,
    SmsService,
    StateService,
    TaskService,
    TeamDealsService,
    LenderUserDealsService,
    TeamService,
    {
      provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
      useValue: {
        appearance: 'outline',
        floatLabel: 'always',
      }
    },
    {
      provide: MAT_SNACK_BAR_DEFAULT_OPTIONS,
      useValue: {
        duration: 3000
      }
    },
    {
      provide: LOCALE_ID,
      deps: [LocaleSettingService],
      useFactory: (
        localeSettingService: LocaleSettingService
      ) => localeSettingService.getLocale()
    },
    {
      provide: DEFAULT_CURRENCY_CODE,
      deps: [LocaleSettingService],
      useFactory: (
        localeSettingService: LocaleSettingService
      ) => localeSettingService.getCurrencyCode()
    },
    {
      provide: ErrorHandler,
      useClass: RollbarErrorHandler
    },
    {
      provide: RollbarService,
      useFactory: rollbarFactory
    },
    provideEnvironmentNgxMask(),
    NgxMaskPipe,
    NgxMaskDirective,
    FormlyModule,
  ],
  bootstrap: [AppComponent]
})
export class AppModule { }
